import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useTheme } from '../../contexts/ThemeContext';

const MCPProxyStatsDashboard = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    mcpServers: [],
    workflows: [],
    summary: {
      totalActiveServerConnections: 0,
      totalServerUsage: 0,
      totalServerAvailability: 0
    }
  });
  const [period, setPeriod] = useState('7d');

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/monitoring/mcp-usage?period=${period}`);
      setStats(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching MCP stats:', err);
      setError('Failed to load MCP usage statistics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatDuration = (ms) => {
    if (!ms) return '0m';
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const StatCard = ({ title, value, subtext, color }) => (
    <div style={{
      backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      flex: 1,
      minWidth: '200px',
      border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
      borderLeft: `4px solid ${color}`
    }}>
      <h3 style={{ 
        fontSize: '0.9rem', 
        color: isDarkMode ? '#aaa' : '#666',
        marginBottom: '0.5rem',
        fontWeight: 'normal' 
      }}>{title}</h3>
      <div style={{ 
        fontSize: '2rem', 
        fontWeight: 'bold',
        color: isDarkMode ? '#fff' : '#333',
        marginBottom: '0.5rem'
      }}>{value}</div>
      {subtext && (
        <div style={{ 
          fontSize: '0.85rem', 
          color: isDarkMode ? '#888' : '#888' 
        }}>{subtext}</div>
      )}
    </div>
  );

  if (loading && !stats.mcpServers.length) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: isDarkMode ? '#ccc' : '#666' }}>
        Loading usage statistics...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#ff6b6b' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 0' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          margin: 0,
          marginBottom: '1rem',
          color: isDarkMode ? 'white' : '#333'
        }}>
          MCP Statistics
        </h2>
        {/* Period Selector */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[
            { value: '7d', label: 'Last 7 Days' },
            { value: '30d', label: 'Last 30 Days' },
            { value: '90d', label: 'Last 90 Days' },
            { value: 'all', label: 'All Time' }
          ].map((periodOption) => (
            <button
              key={periodOption.value}
              onClick={() => setPeriod(periodOption.value)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: period === periodOption.value
                  ? (isDarkMode ? '#0099FF' : '#0099FF')
                  : (isDarkMode ? '#333' : '#e9ecef'),
                color: period === periodOption.value ? 'white' : (isDarkMode ? '#ccc' : '#666'),
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: period === periodOption.value ? '600' : '400',
                transition: 'all 0.2s ease'
              }}
            >
              {periodOption.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'flex', 
        gap: '1.5rem', 
        flexWrap: 'wrap', 
        marginBottom: '2.5rem' 
      }}>
        <StatCard 
          title="Active Connections" 
          value={stats.summary.totalActiveServerConnections} 
          subtext="Currently active MCP sessions"
          color="#10B981"
        />
        <StatCard 
          title="Total Resource Usage" 
          value={stats.summary.totalServerUsage} 
          subtext="Total connection sessions"
          color="#3B82F6"
        />
        <StatCard 
          title="Avg Connection Time" 
          value={formatDuration(stats.mcpServers.reduce((acc, s) => acc + s.totalConnectionTimeMs, 0) / (stats.summary.totalServerUsage || 1))} 
          subtext="Per resource session"
          color="#F59E0B"
        />
      </div>

      {/* MCP Servers Table */}
      <div style={{
        backgroundColor: isDarkMode ? '#2a2a2a' : '#fff',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${isDarkMode ? '#444' : '#e9ecef'}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Resource Usage (MCP Servers)</h2>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ 
                borderBottom: `2px solid ${isDarkMode ? '#444' : '#eee'}`,
                textAlign: 'left'
              }}>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600' }}>Server Name</th>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600', textAlign: 'center' }}>Active</th>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600', textAlign: 'center' }}>Total Connections</th>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600', textAlign: 'right' }}>Total Duration</th>
                <th style={{ padding: '1rem', color: isDarkMode ? '#aaa' : '#666', fontWeight: '600', textAlign: 'center' }}>Unique Users</th>
              </tr>
            </thead>
            <tbody>
              {stats.mcpServers.map((server, index) => (
                <tr key={server.serverId} style={{ 
                  borderBottom: `1px solid ${isDarkMode ? '#333' : '#f5f5f5'}`,
                  backgroundColor: server.activeCount > 0 ? (isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)') : 'transparent'
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 'bold' }}>{server.serverName}</div>
                    <div style={{ fontSize: '0.85rem', color: isDarkMode ? '#888' : '#999' }}>{server.description || 'No description'}</div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      backgroundColor: server.status === 'AVAILABLE' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: server.status === 'AVAILABLE' ? '#10B981' : '#EF4444'
                    }}>
                      {server.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {server.activeCount > 0 ? (
                      <span style={{ 
                        color: '#10B981', 
                        fontWeight: 'bold',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{ 
                          width: '8px', 
                          height: '8px', 
                          borderRadius: '50%', 
                          backgroundColor: '#10B981',
                          display: 'inline-block'
                        }}></span>
                        {server.activeCount}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>{server.usageCount}</td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontFamily: 'monospace' }}>
                    {formatDuration(server.totalConnectionTimeMs)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>{server.uniqueUsageUsers}</td>
                </tr>
              ))}
              {stats.mcpServers.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: isDarkMode ? '#888' : '#999' }}>
                    No resource usage data available for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MCPProxyStatsDashboard;
